/**
 * Core grid library
 *
 * https://www.figma.com/file/zYke5qihweC8yQSpXpcRO2/%F0%9F%93%9000-OM-CORE-Library?node-id=0%3A1
 */

import styled from '@emotion/styled';

// ============================================================================
// README
// ----------------------------------------------------------------------------
// The following objects are a compromise between existing functionality and
// the core figma diagrams.
//
// Discussion should be had before changing them.
// ============================================================================

export const BREAKPOINTS = {
  MOBILE_MAX: '30rem',
  TABLET_MIN: '48rem',
  TABLET_MAX: '64rem',
  DESKTOP_LARGE_MIN: '90rem',
};

/**
 * Use MEDIA_QUERIES as a guide for styles applied to certain devices
 *
 * @example
 *
 * import { MEDIA_QUERIES as M } from '@ordermentum/ui-shared/foundations/grid';
 *
 * const Wrapper = styled('div')({
 *   width: '12rem',
 *   [M.TABLET_LANDSCAPE_AND_SMALLER]: {
 *     width: '6rem',
 *   }
 *   [M.MOBILE_ONLY]: {
 *     width: '4rem',
 *   }
 * });
 *
 * // or with styled strings
 *
 * const Wrapper = styled('div')`
 *   width: 12rem;
 *   ${M.TABLET_LANDSCAPE_AND_SMALLER} {
 *     width: 6rem;
 *   }
 *   ${M.MOBILE_ONLY} {
 *     width: 4rem;
 *   }
 */
export const MEDIA_QUERIES = {
  /**
   * Captures very small devices
   *
   * For most standard mobile phones in portrait mode
   * <= 30rem or 480px
   */
  MOBILE_ONLY: `@media (max-width: ${BREAKPOINTS.MOBILE_MAX})`,
  /**
   * Captures everything from the size of a portrait tablet and smaller
   *
   * For devices such as phablets and tablets in portrait
   * <= 48rem or 768px
   */
  TABLET_PORTRAIT_AND_SMALLER: `@media (max-width: ${BREAKPOINTS.TABLET_MIN})`,
  /**
   * Captures everything from the size of a landscape tablet and smaller
   *
   * For devices such as tablets and small laptops
   * <= 64rem or 1024px
   */
  TABLET_LANDSCAPE_AND_SMALLER: `@media (max-width: ${BREAKPOINTS.TABLET_MAX})`,
  /*
   * Captures everything from a standard sized desktop and smaller
   *
   * For an average desktop monitor
   * <= 90 or 1440px
   */
  SMALL_DESKTOP_AND_SMALLER: `@media (max-width: ${BREAKPOINTS.DESKTOP_LARGE_MIN})`,
} as const;

type validMediaQueries = (typeof MEDIA_QUERIES)[keyof typeof MEDIA_QUERIES];

const generateLargeScreenBreakpointRenderer = (
  component: Parameters<typeof styled>[0]
) =>
  styled(component)<{
    mediaQuery: validMediaQueries;
  }>(p => ({
    [p.mediaQuery]: {
      display: 'none',
    },
  }));

const generateSmallScreenBreakpointRenderer = (
  component: Parameters<typeof styled>[0]
) =>
  styled(component)<{
    mediaQuery: validMediaQueries;
  }>(p => ({
    display: 'none',
    [p.mediaQuery]: {
      display: 'inherit',
    },
  }));

/**
 * Helper component that will only display contents when screen is larger than
 * the given breakpoint
 *
 * @param mediaQuery - a value from the MEDIA_QUERIES object in @ordermentum/ui-shared/foundations/grid to use.
 *
 * @example Will only display HelloWorld when larger than a portrait Tablet
 *
 * <LargeScreenOnly mediaQuery={MEDIA_QUERIES.TABLET_PORTRAIT_AND_SMALLER}>
 *   Hello World
 * </LargeScreenOnly>
 */
export const LargeScreenOnly = generateLargeScreenBreakpointRenderer('div');

/**
 * Helper component that will only display contents when screen is smaller than
 * the given breakpoint
 *
 * @param mediaQuery - a value from the MEDIA_QUERIES object in @ordermentum/ui-shared/foundations/grid to use.
 *
 * @example Will only display HelloWorld when smaller than a portrait tablet
 *
 * <SmallScreenOnly mediaQuery={MEDIA_QUERIES.TABLET_PORTRAIT_AND_SMALLER}>
 *   Hello World
 * </SmallScreenOnly>
 */
export const SmallScreenOnly = generateSmallScreenBreakpointRenderer('div');
